/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, April 2020
 */

import React, { useCallback, useContext, useEffect } from 'react';
import { Box, Platform, preloadImage } from '@staffino/primitive-controls';
import EmoticonButton from '../components/emoticon-button';
import SettingsContext from '../settings-context';

const emoticonUrl = (i: number) =>
  `https://s3.eu-west-1.amazonaws.com/cdn-static.staffino.com/images/web/icon-emoji-${i}.svg`;
const emoticonOffUrl = (i: number) =>
  `https://s3.eu-west-1.amazonaws.com/cdn-static.staffino.com/images/web/icon-emoji-${i}-off.svg`;

type Props = {
  name: string;
  lowerBoundLabel?: string;
  upperBoundLabel?: string;
  lowerBoundScore?: number;
  upperBoundScore?: number;
  steps?: number;
  value?: number;
  onChange?: (score: number) => void;
} & React.ComponentProps<typeof Box>;

const EmoticonsControl: React.ForwardRefExoticComponent<
  Props & React.RefAttributes<unknown>
> = React.forwardRef(
  (
    { name, upperBoundScore, value, onChange, ...rest }: Props,
    ref: React.Ref<unknown>,
  ) => {
    const { contentWidth } = useContext(SettingsContext);
    const selectedScore = parseInt(value?.toString(), 10);

    useEffect(() => {
      // preload animations
      [1, 2, 3, 4, 5].forEach((i) => preloadImage(emoticonOffUrl(i)));
    });

    const emoticonsCount = upperBoundScore - 1 + 1;
    const emoticonWidth = Platform.select<number | string>({
      native: (contentWidth * 0.8) / (emoticonsCount || 1),
      default: `${Math.floor(10000 / (emoticonsCount || 1)) / 100}%`,
    });

    const handleChange = useCallback(
      (score) => onChange?.(parseInt(score, 10)),
      [onChange],
    );

    if (emoticonsCount <= 0) {
      return null;
    }

    return (
      <Box {...rest} flexDirection={'row' as const} justifyContent="center">
        {(upperBoundScore === 5 ? [1, 2, 3, 4, 5] : [1, 3, 5]).map(
          (scoreNo) => (
            <EmoticonButton
              key={scoreNo}
              innerRef={ref}
              name={name}
              score={scoreNo}
              width={emoticonWidth}
              imageUrl={
                selectedScore === scoreNo || !selectedScore
                  ? emoticonUrl(scoreNo)
                  : emoticonOffUrl(scoreNo)
              }
              onChange={handleChange}
            />
          ),
        )}
      </Box>
    );
  },
);

export default EmoticonsControl;
